import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, fileUrl } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Trash, PencilSimple, FileText, ArrowsLeftRight, Star, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const empty = { name: "", company: "", rfc: "", specialty: "", phone: "", email: "", is_key: false };

export default function Contractors() {
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docFor, setDocFor] = useState(null);
  const navigate = useNavigate();
  const toSupplier = async (c) => {
    if (!window.confirm(`¿Enviar "${c.name}" a Proveedores? Se moverá su expediente. (No debe tener contratos activos.)`)) return;
    try { await api.post(`/contractors/${c.id}/convert-to-supplier`); toast.success("Enviado a Proveedores"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleKey = async (c) => {
    try { await api.put(`/contractors/${c.id}`, { ...c, is_key: !c.is_key }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadDoc = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    try { const r = await api.post(`/contractors/${docFor.id}/fiscal-doc`, fd); toast.success("Documento subido"); setDocFor(r.data); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = () => api.get("/contractors").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/contractors/${editId}`, form);
      else await api.post("/contractors", form);
      toast.success("Contratista guardado");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar contratista?")) return;
    await api.delete(`/contractors/${id}`); toast.success("Eliminado"); load();
  };

  return (
    <div>
      <PageHeader overline="Directorio" title="Contratistas"
        action={isManager && <PrimaryButton onClick={openNew} data-testid="add-contractor-btn"><Plus size={18} weight="bold" /> Nuevo</PrimaryButton>} />

      <div className="flex gap-2 mb-6" data-testid="directory-tabs">
        <span className="px-4 py-1.5 rounded-sm text-sm font-semibold bg-primary text-white">Contratistas</span>
        <button onClick={() => navigate("/proveedores")} data-testid="tab-proveedores" className="px-4 py-1.5 rounded-sm text-sm font-semibold border border-input text-muted-foreground hover:bg-muted transition-colors">Proveedores</button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Users} title="Sin contratistas" subtitle="Registra a las empresas o personas que ejecutan la obra."
          action={isManager && <PrimaryButton onClick={openNew}><Plus size={18} weight="bold" /> Agregar contratista</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <Card key={c.id} data-testid={`contractor-card-${c.id}`} className="p-6 hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading font-bold text-lg">{c.name}</div>
                  <div className="text-sm text-muted-foreground">{c.company || "—"}</div>
                  {(c.constancia_fiscal && c.ident_representante && c.comprobante_domicilio) ? (
                    <div data-testid={`contractor-docs-complete-${c.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm"><CheckCircle size={12} weight="fill" className="text-emerald-500" /> Documentación completa</div>
                  ) : (
                    <div data-testid={`contractor-docs-missing-${c.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-sm"><WarningCircle size={12} weight="fill" className="text-red-500" /> Faltan documentos legales</div>
                  )}
                  <div><button onClick={() => toggleKey(c)} data-testid={`key-toggle-${c.id}`} className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm transition-colors ${c.is_key ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}><Star size={11} weight={c.is_key ? "fill" : "regular"} /> {c.is_key ? "Clave" : "Marcar clave"}</button></div>
                </div>
                {isManager && (
                  <div className="flex gap-1">
                    <button onClick={() => setDocFor(c)} data-testid={`contractor-docs-${c.id}`} title="Documentos fiscales" className="p-1.5 text-muted-foreground hover:text-primary"><FileText size={18} /></button>
                    <button onClick={() => openEdit(c)} className="p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                    {canDelete && <button onClick={() => remove(c.id)} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-1 text-sm">
                {c.specialty && <div><span className="overline">Especialidad</span><div>{c.specialty}</div></div>}
                {c.rfc && <div className="text-muted-foreground">RFC: {c.rfc}</div>}
                {c.phone && <div className="text-muted-foreground">Tel: {c.phone}</div>}
                {c.email && <div className="text-muted-foreground">{c.email}</div>}
              </div>
              {isManager && <button onClick={() => toSupplier(c)} data-testid={`to-supplier-${c.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><ArrowsLeftRight size={14} weight="bold" /> Enviar a Proveedores</button>}
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar contratista" : "Nuevo contratista"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nombre *"><input data-testid="contractor-name" required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Empresa"><input className={inputClass} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="RFC"><input className={inputClass} value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value })} /></Field>
            <Field label="Especialidad"><input className={inputClass} value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Teléfono"><input className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Correo"><input className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" data-testid="contractor-key" checked={!!form.is_key} onChange={(e) => setForm({ ...form, is_key: e.target.checked })} /> Contratista clave (alerta de asistencia si no registra entrada)</label>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-contractor-btn">{busy ? "Guardando…" : "Guardar"}</PrimaryButton>
        </form>
      </Modal>

      <Modal open={!!docFor} onClose={() => setDocFor(null)} title={`Documentos · ${docFor?.name || ""}`}>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Sube la documentación legal de la empresa. Puedes reemplazarla cuando quieras.</p>
          {[["constancia_fiscal", "Constancia de Situación Fiscal"], ["ident_representante", "Identificación del representante legal"], ["comprobante_domicilio", "Comprobante de domicilio"]].map(([k, label]) => (
            <div key={k} className="border border-input rounded-sm p-3">
              <div className="text-sm font-semibold mb-1">{label}</div>
              {docFor?.[k]?.path ? <a href={fileUrl(docFor[k].path)} target="_blank" rel="noreferrer" data-testid={`contractor-doc-view-${k}`} className="text-xs text-success font-semibold hover:underline">✓ Ver: {docFor[k].name}</a> : <span className="text-xs text-muted-foreground">Sin archivo</span>}
              <input type="file" accept="application/pdf,image/*" data-testid={`contractor-doc-${k}`} onChange={(e) => uploadDoc(k, e.target.files[0])}
                className="mt-2 w-full text-xs file:mr-2 file:px-2 file:py-1 file:border-0 file:bg-primary file:text-white file:rounded-sm" />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
